import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { BellRing, RefreshCcw, ShieldCheck, TriangleAlert } from "lucide-react"
import {
  forceResubscribeToPush,
  type IPushValidationResult,
  validatePushSubscription,
} from "../../pwa/pushNotifications"
import { registerServiceWorker } from "../../pwa/registerServiceWorker"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"

async function ensureActiveServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) {
    return null
  }

  const existing = await navigator.serviceWorker.getRegistration("/sw.js")

  const registration = existing ?? (await registerServiceWorker())

  if (!registration) {
    return null
  }

  if (registration.active) {
    return registration
  }

  const worker = registration.installing ?? registration.waiting
  if (!worker) {
    return registration
  }

  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error("timeout_sw_activation")), 10000)

    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") {
        window.clearTimeout(timeout)
        resolve()
      }
    })
  }).catch(() => null)

  return registration
}

export function PushNotificationsPage() {
  const { t } = useTranslation("pages")
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [validation, setValidation] = useState<IPushValidationResult | null>(null)

  const isValidationOk = useMemo(() => {
    if (!validation) {
      return false
    }

    return (
      validation.hasServiceWorkerSupport &&
      validation.hasNotificationSupport &&
      validation.hasPushManagerSupport &&
      validation.notificationPermission === "granted" &&
      validation.hasBrowserSubscription &&
      validation.syncedWithServer
    )
  }, [validation])

  const handleValidate = async () => {
    setIsLoading(true)
    setMessage("")

    try {
      const registration = await ensureActiveServiceWorkerRegistration()

      if (!registration) {
        setMessage(t("push_notification_status_error_no_sw"))
        return
      }

      const result = await validatePushSubscription(registration)

      setValidation(result)

      if (result.syncedWithServer) {
        setMessage(t("push_notification_status_success"))
      } else {
        setMessage(t("push_notification_status_warning"))
      }
    } catch {
      setMessage(t("push_notification_status_error"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleForceResubscribe = async () => {
    setIsLoading(true)
    setMessage("")

    try {
      if (!("Notification" in window)) {
        setMessage(t("push_notification_status_error_not_supported"))
        return
      }

      const permission =
        Notification.permission === "granted" ? "granted" : await Notification.requestPermission().catch(() => "denied")

      if (permission !== "granted") {
        setMessage(t("push_notification_status_error_permission"))
        return
      }

      const registration = await ensureActiveServiceWorkerRegistration()

      if (!registration) {
        setMessage(t("push_notification_status_error_no_sw"))
        return
      }

      const subscribed = await forceResubscribeToPush(registration).catch(() => false)

      if (!subscribed) {
        setMessage(t("push_notification_resubscribe_fail"))
        return
      }

      const result = await validatePushSubscription(registration)

      setValidation(result)
      setMessage(
        result.syncedWithServer ? t("push_notification_resubscribe_success") : t("push_notification_status_warning"),
      )
    } catch {
      setMessage(t("push_notification_resubscribe_fail"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full h-[calc(100%-25px)] overflow-y-auto p-3 sm:p-4 md:p-5">
      <Animate type={AnimationTypes.fadeInTraslate}>
        <div className="w-full overflow-hidden rounded-lg border-2 border-orange-200 bg-white ">
          <div className="border-b border-orange-100 bg-gradient-to-r from-orange-50 via-amber-50 to-white p-5">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl border border-orange-200 bg-white p-2 text-orange-600 shadow-sm">
                <BellRing size={20} />
              </div>

              <div>
                <h2 className="text-lg font-black text-slate-800">{t("push_notification_page_title")}</h2>

                <p className="mt-1 text-sm text-slate-600">{t("push_notification_page_description")}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <button
                disabled={isLoading}
                onClick={() => void handleValidate()}
                className="group inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl border-2 border-sky-200 bg-gradient-to-br from-white to-sky-200 px-4 py-3 text-sm font-extrabold text-sky-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
                data-interaction-description-key="interactionDescriptions:pages.notifications.pushNotificationsPage.button.voidHandleValidate"
              >
                <ShieldCheck size={16} className="text-sky-600" />
                <span>{isLoading ? t("push_notification_loading") : t("push_notification_validate_button")}</span>
              </button>

              <button
                disabled={isLoading}
                onClick={() => void handleForceResubscribe()}
                className="group inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl border-2 border-orange-400 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 text-sm font-extrabold text-white shadow-[0_8px_18px_rgba(249,115,22,0.35)] transition-all duration-200 hover:-translate-y-0.5 hover:brightness-110 hover:shadow-[0_10px_24px_rgba(249,115,22,0.45)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
                data-interaction-description-key="interactionDescriptions:pages.notifications.pushNotificationsPage.button.voidHandleForceResubscribe"
              >
                <RefreshCcw size={16} className={isLoading ? "animate-spin" : ""} />
                <span>{isLoading ? t("push_notification_loading") : t("push_notification_resubscribe_button")}</span>
              </button>
            </div>

            <Animate type={AnimationTypes.fadeInTraslate}>
              <div className="rounded-xl border border-dashed border-orange-200 bg-orange-50/50 px-3 py-2 text-xs text-orange-700">
                {t("push_notification_hint")}
              </div>
            </Animate>

            {message && (
              <Animate type={AnimationTypes.fadeInTraslate}>
                <div
                  className={`rounded-xl border px-3 py-3 text-sm font-semibold ${
                    isValidationOk
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-orange-200 bg-orange-50 text-orange-700"
                  }`}
                >
                  {message}
                </div>
              </Animate>
            )}

            {validation && (
              <Animate type={AnimationTypes.fadeInTraslate}>
                <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                  <StatusItem label={t("push_notification_check_sw")} ok={validation.hasServiceWorkerSupport} />

                  <StatusItem
                    label={t("push_notification_check_notification_api")}
                    ok={validation.hasNotificationSupport}
                  />
                  <StatusItem label={t("push_notification_check_push_manager")} ok={validation.hasPushManagerSupport} />

                  <StatusItem
                    label={t("push_notification_check_permission")}
                    ok={validation.notificationPermission === "granted"}
                  />

                  <StatusItem
                    label={t("push_notification_check_browser_subscription")}
                    ok={validation.hasBrowserSubscription}
                  />

                  <StatusItem label={t("push_notification_check_server_sync")} ok={validation.syncedWithServer} />
                </div>
              </Animate>
            )}
          </div>
        </div>
      </Animate>
    </div>
  )
}

function StatusItem({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
    >
      {ok ? <ShieldCheck size={16} className="text-green-600" /> : <TriangleAlert size={16} className="text-red-600" />}
      <span className={`${ok ? "text-green-700" : "text-red-700"}`}>{label}</span>
    </div>
  )
}
